/**
 * Detects that OUR backend is unavailable, as opposed to the user being offline.
 *
 * A dead gateway and a dead connection look identical to Axios: both surface as
 * ERR_NETWORK with no response, because the browser never exposes the CORS
 * preflight to JavaScript. Guessing from the error alone is how a backend outage
 * ends up showing "you appear to be offline" (see
 * incidents/2026-09-01-prod-action-center-gateway.md).
 *
 * So instead of guessing, we ask the server. Every gateway exposes an unauthenticated
 * /api/v1/__health route (functions/src/http/gatewayFactory.js). A fetch with no
 * Authorization and no custom headers is a CORS simple request, so it is not
 * preflighted and answers whether that container is alive.
 *
 * The probe runs ONLY against a request that has already been pending past the
 * watchdog. There is no polling and no timer in the background: this service is a
 * guard on top of the existing offline detection, never a replacement for it.
 * Requests are never aborted, so endpoints that legitimately run for minutes
 * (bulk reminders, platform-admin exports) are unaffected.
 */

import connectionService from './ConnectionService'

const HEALTH_PATH = '/api/v1/__health'

// How long a single request may stay pending before we ask the gateway whether it
// is alive. Well above a slow cold start, well below the browser giving up.
const WATCHDOG_MS = 15000

// The health route is a trivial JSON response; anything slower than this is as
// good as unreachable.
const PROBE_TIMEOUT_MS = 5000

/**
 * Derives the gateway root from a request URL, e.g.
 *   https://us-central1-x.cloudfunctions.net/actionCenterGateway/api/v1/plan-years
 *   -> https://us-central1-x.cloudfunctions.net/actionCenterGateway
 * Everything before the /api/ segment is kept, so the emulator's
 * /<project>/<region>/<gateway> prefix survives too.
 * @param {string} url
 * @returns {string|null} Gateway root, or null when the URL is not a gateway call.
 */
export const gatewayRootFromUrl = (url) => {
  let parsed
  try {
    parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined)
  } catch (error) {
    return null
  }

  const segments = parsed.pathname.split('/').filter(Boolean)
  const apiIndex = segments.indexOf('api')
  if (apiIndex < 1) return null

  return `${parsed.origin}/${segments.slice(0, apiIndex).join('/')}`
}

class ServerHealthService {
  constructor() {
    this.serverDegraded = false
    this.listeners = []
    this.watchdogs = new Map() // watch id -> timer
    this.watchSeq = 0
    this.probesInFlight = new Map() // gateway root -> Promise<boolean>
  }

  /**
   * Start the watchdog for one request. Safe to call for every request: the timer
   * is cleared on completion, so healthy traffic never triggers a probe.
   * @param {Object} config - Axios request config, stamped with the watch id.
   */
  watchRequest(config) {
    if (!config || typeof window === 'undefined') return

    const gatewayRoot = gatewayRootFromUrl(config.url || '')
    if (!gatewayRoot) return

    const id = ++this.watchSeq
    config.__serverHealthWatchId = id
    this.watchdogs.set(
      id,
      setTimeout(() => {
        this.watchdogs.delete(id)
        this.evaluateGateway(gatewayRoot)
      }, WATCHDOG_MS)
    )
  }

  /**
   * Stop the watchdog for a request that finished, successfully or not.
   * @param {Object} config - Axios request config.
   */
  unwatchRequest(config) {
    const id = config?.__serverHealthWatchId
    if (!id) return
    const timer = this.watchdogs.get(id)
    if (timer) clearTimeout(timer)
    this.watchdogs.delete(id)
  }

  /**
   * A request has been hanging: ask the gateway whether it is alive, and only then
   * decide. A failed probe still has to clear the connectivity check, otherwise a
   * stalled client connection would be reported as a backend outage.
   * @param {string} gatewayRoot
   */
  async evaluateGateway(gatewayRoot) {
    if (this.serverDegraded) return

    const healthy = await this.probeGateway(gatewayRoot)
    if (healthy) return

    // checkActiveConnection is a pure probe: it never mutates the offline state,
    // so reading it here cannot interfere with the existing offline modal.
    const reachable = await connectionService.checkActiveConnection()
    if (!reachable) return

    this.reportServerDegraded()
  }

  /**
   * @param {string} gatewayRoot
   * @returns {Promise<boolean>} True when the gateway answered its health route.
   */
  probeGateway(gatewayRoot) {
    const existing = this.probesInFlight.get(gatewayRoot)
    if (existing) return existing

    const probe = this.runProbe(gatewayRoot).finally(() => {
      this.probesInFlight.delete(gatewayRoot)
    })
    this.probesInFlight.set(gatewayRoot, probe)
    return probe
  }

  async runProbe(gatewayRoot) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)

    try {
      // No headers on purpose: adding any would make this a preflighted request
      // and we would be testing the CORS handshake instead of the container.
      const response = await fetch(`${gatewayRoot}${HEALTH_PATH}`, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal
      })
      return response.ok
    } catch (error) {
      return false
    } finally {
      clearTimeout(timer)
    }
  }

  /**
   * Notifies listeners only on the transition into the degraded state.
   */
  reportServerDegraded() {
    if (this.serverDegraded) return
    this.serverDegraded = true
    this.notifyListeners()
  }

  /**
   * Called when any API call succeeds, which proves the backend is answering again.
   * Notifies listeners only on the transition out of the degraded state.
   */
  reportServerRecovered() {
    if (!this.serverDegraded) return
    this.serverDegraded = false
    this.notifyListeners()
  }

  isServerDegraded() {
    return this.serverDegraded
  }

  registerListener(callback) {
    if (typeof callback === 'function' && !this.listeners.includes(callback)) {
      this.listeners.push(callback)
    }
  }

  unregisterListener(callback) {
    const index = this.listeners.indexOf(callback)
    if (index !== -1) {
      this.listeners.splice(index, 1)
    }
  }

  notifyListeners() {
    this.listeners.forEach(callback => {
      callback(this.serverDegraded)
    })
  }
}

const serverHealthService = new ServerHealthService()

export default serverHealthService
